import * as React from 'react';

import {ConfirmationDialog} from 'shells/confirmation-dialog/confirmation-dialog';
import {ListRowWideWithHeader} from 'shells/list-row/list-row-wide-with-header';
import {RelativeTimestamp} from 'shells/timestamp/relative-timestamp';

import type {FormStatus} from 'nutshell-graphql-types';
import type {GetMcfxForms_mcfxForms_edges_node_wfForm_publishedVersion_fields as Field} from 'nutshell-graphql-types';

import type {FormFieldSchema} from '../../core/nutshell-fields/types';

import {getFormStatus} from '../helpers';

import {ConnectedFormsPreview} from './connected-forms-preview';
import {getSubmissionCountText} from './helpers';

type DialogType = 'ignore' | 'deactivate';

type Props = {
    name: string;
    htmlUrlPath: string;
    numSubmissions: number;
    lastSubmittedTime: any | null | undefined;
    status: FormStatus;
    fields: Field[];
    formSchema: FormFieldSchema;
    ignoreForm: () => Promise<any>;
    unignoreForm: () => Promise<any>;
};

export function ConnectedFormsListItem(props: Props) {
    const [confirmationDialogType, setConfirmationDialogType] = React.useState<
        DialogType | null | undefined
    >(null);
    const [errorMessage, setErrorMessage] = React.useState<string | null | undefined>(null);

    const getOptions = () => {
        const options = [];
        if (props.status === 'ACTIVATED') {
            options.push({
                label: 'Deactivate form',
                handleSelect: () => {
                    setConfirmationDialogType('deactivate');
                },
            });
        } else if (props.status === 'IGNORED') {
            options.push({
                label: 'Unignore form',
                handleSelect: () => {
                    props.unignoreForm();
                },
            });
        } else {
            options.push({
                label: 'Ignore form',
                handleSelect: () => {
                    setConfirmationDialogType('ignore');
                },
            });
        }

        return options;
    };

    const handleIgnoreForm = () => {
        props
            .ignoreForm()
            .then(() => {
                setConfirmationDialogType(null);
            })
            .catch(() => {
                setErrorMessage('Oops! Something went wrong while ignoring this form.');
            });
    };

    const handleDeactivateForm = () => {
        props
            .unignoreForm()
            .then(() => {
                setConfirmationDialogType(null);
            })
            .catch(() => {
                setErrorMessage('Oops! Something went wrong while deactivating this form.');
            });
    };

    return (
        <ListRowWideWithHeader
            name={props.name}
            to={props.htmlUrlPath}
            metadataPrimary={getSubmissionCountText(props.numSubmissions)}
            metadataSecondary={
                props.lastSubmittedTime && typeof props.lastSubmittedTime === 'number' ? (
                    <span className='flex'>
                        Last submitted&nbsp;
                        <RelativeTimestamp datetime={props.lastSubmittedTime} />
                    </span>
                ) : undefined
            }
            // @ts-expect-error
            status={getFormStatus(props.status)}
            ignoreForm={props.ignoreForm}
            options={getOptions()}
        >
            <ConnectedFormsPreview
                fields={props.fields}
                formSchema={props.formSchema}
                htmlUrlPath={props.htmlUrlPath}
                isFormIgnored={props.status === 'IGNORED'}
            />
            {confirmationDialogType ? (
                <ConfirmationDialog
                    title={`Are you sure you want to ${confirmationDialogType} this form?`}
                    message={
                        confirmationDialogType === 'ignore'
                            ? 'When ignored, we’ll stop collecting data from this form.'
                            : 'New submissions will no longer be processed until you reactivate this form.'
                    }
                    confirmText={`Yes, ${confirmationDialogType} form`}
                    cancelText='No, cancel'
                    // @ts-expect-error
                    errorMessage={errorMessage}
                    onCancel={() => {
                        setErrorMessage(null);
                        setConfirmationDialogType(null);
                    }}
                    onConfirm={() => {
                        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                        confirmationDialogType === 'ignore'
                            ? handleIgnoreForm()
                            : handleDeactivateForm();
                    }}
                />
            ) : undefined}
        </ListRowWideWithHeader>
    );
}
